import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, TextField, Typography } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import FormWrapper from "src/components/FormWrapper";
import { FormProvider, RHFTextField } from "src/components/hook-form";
import { restrict } from "src/components/restrict";
import { HOST_API } from "src/config-prod";
import { addRider, uploadImage } from "src/pages/api";
import * as Yup from "yup"; 

const defaultValues = {
  code: "",
  fullName: "",
  mobile: "",
  doc_type: "",
  document_number: "",
  username: "",
  password: "",
};
const schema = Yup.object().shape({
  code: Yup.string().trim().required("Required"),
  fullName: Yup.string().trim().required("Required"),
  doc_type: Yup.string().trim().required("Required"),
  mobile: Yup.string()
    .trim()
    .required("Required")
    .matches(/^[6789]\d{9}$/, "Invalid Mobile Number"),
  document_number: Yup.string().trim().required("Required"),
  username: Yup.string().trim().required("Required"),
  password: Yup.string().trim().required("Required"),
});
export default function AddRider({ onClose, row, isEditMode }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(null);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { handleSubmit, reset, getValues } = methods;

  const mutation = useMutation(req => addRider({ req }), {
    onSuccess: () => {
      enqueueSnackbar(`Rider ${isEditMode ? "Updated" : "Added"}`, {
        variant: "success",
      });
      queryClient.invalidateQueries(["getAllRider"]);
      onClose();
    },
    onError: error => {
      enqueueSnackbar(error?.message || "Failed to Add", { variant: "error" });
    },
  });

  const handleCheckUser = async () => {
    setLoading(true);
    try {
      const req = {
        tableName: "rider",
        username: getValues("username"),
      };
      const { data } = await axios.post(HOST_API + "master/username/get", req);
      if (data && data?.data[0].flag === 1) {
        enqueueSnackbar(
          "This username is already taken. Please try using different",
          {
            variant: "error",
          }
        );
        return false;
      } else {
        return true;
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };
  const onSubmit = async data => {
    if (!isEditMode) {
      const isValid = await handleCheckUser();
      if (!isValid) return;
    }
  
    let req = {};
    if (isEditMode) {
      req = {
        ...data,
        filename: row.filename,
      };
    } else {
      req = { ...data };
    }
  
    if (isEditMode) {
      if (selectedFile) {
        const formData = new FormData();
        formData.append("image", selectedFile);
        uploadImage("riderDocument", formData)
          .then(resData => {
            req.filename = resData.image_id;
            mutation.mutate(req);
          })
          .catch(error => {
            enqueueSnackbar(error?.message || "Failed to Add", {
              variant: "error",
            });
          });
        return;
      }
      mutation.mutate(req);
      return;
    }
  
    if (!isEditMode && selectedFile) {
      const formData = new FormData();
      formData.append("image", selectedFile);
      uploadImage("riderDocument", formData)
        .then(resData => {
          req.filename = resData.image_id;
          mutation.mutate(req);
        })
        .catch(error => {
          enqueueSnackbar(error?.message || "Failed to Add", {
            variant: "error",
          });
        });
      return;
    }
    enqueueSnackbar("Please Select an image", {
      variant: "error",
    });
  };
  

  useEffect(() => {
    if (isEditMode) {
      reset({
        id: row?.id,
        code: row.code,
        delivery_company_id: row.delivery_company_id,
        fullName: row?.fullName,
        mobile: row.mobile,
        doc_type: row.doc_type,
        document_number: row.document_number,
        username: row.username,
        password: row.password,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, row]);

  return (
    <FormWrapper
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => {
        reset();
      }}
      loading={mutation.isLoading}
      maxWidth={"md"}
      fullWidth
      isEditMode={isEditMode}
      title={isEditMode ? "Update Rider" : "Add Rider"}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <RHFTextField name={"code"} label={"Code"} required />
          </Grid>
          <Grid item xs={4}>
            <RHFTextField name={"fullName"} label={"Full name"} required />
          </Grid>
          <Grid item xs={4}>
            <RHFTextField
              name={"mobile"}
              label={"Mobile No."}
              inputProps={{ maxLength: 10 }}
              required
              onInput={restrict.digits}
            />
          </Grid>
          <Grid item xs={4}>
            <RHFTextField name={"doc_type"} label={"Document Type"} required />
          </Grid>
          <Grid item xs={4}>
            <RHFTextField
              name={"document_number"}
              label={"Document Number"}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name={"image"}
              label={"Upload Document"}
              type="file"
              InputLabelProps={{ shrink: true }}
              fullWidth
              onChange={e => {
                setSelectedFile(e.target.files[0]);
              }}
            />
            {isEditMode && (
              <Typography
                sx={{
                  marginTop: "10px",
                  cursor: "pointer",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
                onClick={() => {
                  window.open(
                    `${HOST_API}images/riderDocument/${row?.filename}`,
                    "_blank"
                  );
                }}
              >
                {row?.filename}
              </Typography>
            )}
          </Grid>
          <Grid item xs={4}>
            <RHFTextField
              name={"username"}
              label={"User Name"}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={4}>
            <RHFTextField
              name={"password"}
              label={"Password"}
              fullWidth
              required
            />
          </Grid>
        </Grid>
      </FormProvider>
    </FormWrapper>
  );
}
